import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Package, Download, Sparkles, ShoppingBag, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ToolDetail() {
  const { toolId } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/tools/${toolId}`).then(({ data }) => setTool(data)).catch(() => toast.error('Tool not found')).finally(() => setLoading(false));
  }, [toolId]);

  const buy = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/tools/${toolId}` } });
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post(`/tools/${toolId}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = 'Could not start checkout';
      if (status === 401) msg = 'Please log in again to complete your purchase.';
      else if (status === 400 && detail) msg = detail;                         // "no file attached", "already own", etc.
      else if (status === 404) msg = 'This tool is no longer available.';
      else if (status >= 500) msg = 'Checkout is temporarily unavailable. Please try again in a moment.';
      else if (typeof detail === 'string' && detail) msg = detail;
      toast.error(msg);
      setBuying(false);
    }
  };

  const download = async () => {
    try {
      // Ask the server for a short-lived signed URL, then let the browser
      // handle the actual download natively. This preserves the original
      // file extension (docx/zip/mp3/etc.) and works reliably on iPad Safari,
      // where blob-based downloads can be silently blocked.
      const { data } = await api.post(`/tools/${toolId}/download-url`);
      // Direct navigation triggers a native download honoring Content-Disposition.
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download');
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} /></div>
        <Footer />
      </div>
    );
  }
  if (!tool) return null;

  const effective = tool.effective_price_cents;
  const showDiscount = tool.is_member_discounted;

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/tools" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="tool-back">
          <ArrowLeft className="w-4 h-4" /> Back to Sacred Tools
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="rounded-3xl overflow-hidden aspect-square" style={{ background: tool.cover_url ? `url(${tool.cover_url}) center/cover` : `linear-gradient(135deg, ${tool.topic?.tint || '#E57A44'}30 0%, ${tool.topic?.tint || '#E57A44'}10 100%)`, boxShadow: '0 12px 40px rgba(0,0,0,0.06)' }} data-testid="tool-cover">
            {!tool.cover_url && (
              <div className="h-full flex items-center justify-center">
                <Package className="w-24 h-24" style={{ color: tool.topic?.tint || '#E57A44', opacity: 0.5 }} />
              </div>
            )}
          </div>

          <div>
            {tool.topic && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ backgroundColor: `${tool.topic.tint}20`, color: tool.topic.tint }}>
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-xs font-bold uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>{tool.topic.name}</span>
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', lineHeight: 1.15 }} data-testid="tool-title">
              {tool.title}
            </h1>
            <p className="text-base mb-8 whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.8 }}>
              {tool.description}
            </p>

            <div className="p-6 rounded-3xl mb-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 8px 30px rgba(0,0,0,0.03)' }}>
              {tool.owned ? (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Check className="w-5 h-5" style={{ color: '#78A18E' }} />
                    <span className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C715E' }}>
                      {tool.admin_access ? 'Admin access — free download' : 'You own this tool'}
                    </span>
                  </div>
                  <button onClick={download} className="w-full py-3 rounded-full flex items-center justify-center gap-2 transition hover:-translate-y-0.5"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }}
                    data-testid="tool-download-btn">
                    <Download className="w-4 h-4" /> Download now
                  </button>
                </div>
              ) : (
                <div>
                  <div className="mb-5">
                    {showDiscount && (
                      <div className="text-sm line-through" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>${(tool.price_cents / 100).toFixed(2)}</div>
                    )}
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#E57A44' }}>${(effective / 100).toFixed(2)}</span>
                      {showDiscount && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#78A18E', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.05em' }}>Sanctuary price</span>}
                    </div>
                    <p className="text-xs mt-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>One-time purchase · Instant download after checkout</p>
                  </div>
                  <button onClick={buy} disabled={buying} className="w-full py-3 rounded-full flex items-center justify-center gap-2 transition hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700, fontSize: '15px' }}
                    data-testid="tool-buy-btn">
                    <ShoppingBag className="w-4 h-4" /> {buying ? 'Opening checkout…' : `Buy for $${(effective / 100).toFixed(2)}`}
                  </button>
                  {!showDiscount && user?.sanctuary_status !== 'active' && user?.role !== 'admin' && tool.member_price_cents && tool.member_price_cents < tool.price_cents && (
                    <p className="text-xs mt-3 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                      Sanctuary members pay <strong style={{ color: '#78A18E' }}>${(tool.member_price_cents / 100).toFixed(2)}</strong>. <Link to="/sanctuary" className="underline" style={{ color: '#E57A44', fontWeight: 600 }}>Join to save →</Link>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
